import {
  AutomationEventType,
  AutomationEventTypes,
  EventPayload,
  ServerSideEventType,
} from '@/hooks/api/sse/types.ts';
import { useBaseSSE } from '@/hooks/api/sse/useBaseSSE.ts';
import useHandleEvent from '@/hooks/api/sse/useHandleEvent.ts';
import useInvalidateAutomation from '@/hooks/api/sse/automationEvents/useInvalidateAutomation.ts';
import useInvalidateAutomationVersion from '@/hooks/api/sse/automationEvents/useInvalidateAutomationVersion.ts';
import useInvalidateStaticDataset from '@/hooks/api/sse/automationEvents/useInvalidateStaticDataset.ts';
import useInvalidateLinkedDataset from '@/hooks/api/sse/automationEvents/useInvalidateLinkedDataset.ts';
import useInvalidateDataset from '@/hooks/api/sse/automationEvents/useInvalidateDataset.ts';
import useInvalidateRun from '@/hooks/api/sse/automationEvents/useInvalidateRun.ts';
import { useCallback, useMemo } from 'react';
import useAutomationId from '@/hooks/useAutomationId.ts';
import {
  AUTOMATION_BASE_KEY,
  STATIC_DATASETS_KEY,
  STATIC_DATASET_USAGE_KEY,
} from '@/utils/queryKeys.ts';
import { useQueryClient } from '@tanstack/react-query';
import useInvalidateIntegrationSelectFieldOptions, {
  SelectFieldOptionsPayload,
} from '@/hooks/api/sse/automationEvents/useInvalidateIntegrationSelectFieldOptions.ts';
import { Automation, AutomationVersion } from '@/types/automations.ts';
import { Execution, Run } from '@/types/runs.ts';
import { Dataset, LinkedDataset, StaticDataset } from '@/types/datasets.ts';
import useInvalidateExecution from '@/hooks/api/sse/automationEvents/useInvalidateExecution.ts';
import useRetrieveAutomation from '@/hooks/api/automations/useRetrieveAutomation.ts';

// Get all enum values (event type strings)
const eventTypes = Object.values(AutomationEventTypes) as AutomationEventType[];

export function useAutomationSSE() {
  const automationId = useAutomationId();

  const queryClient = useQueryClient();

  const { data: automation } = useRetrieveAutomation();

  const enabled = useMemo(() => !!automation, [automation]);

  const invalidateAutomation = useInvalidateAutomation(automationId);
  const invalidateAutomationVersion = useInvalidateAutomationVersion(automationId);
  const invalidateStaticDataset = useInvalidateStaticDataset(automationId);
  const invalidateLinkedDataset = useInvalidateLinkedDataset(automationId);
  const invalidateDataset = useInvalidateDataset();
  const invalidateRun = useInvalidateRun(automationId);
  const invalidateExecution = useInvalidateExecution(automationId);
  const invalidateSelectFieldOptions = useInvalidateIntegrationSelectFieldOptions(automationId);

  const getKeysToInvalidate = useCallback(
    (type: ServerSideEventType, data: EventPayload): unknown[][] | undefined => {
      switch (type) {
        case AutomationEventTypes.Automation:
          return invalidateAutomation(data.payload as Automation);
        case AutomationEventTypes.AutomationVersion:
          return invalidateAutomationVersion(data.payload as AutomationVersion);
        case AutomationEventTypes.Dataset:
          return invalidateDataset(data.payload as Dataset);
        case AutomationEventTypes.StaticDataset:
          return invalidateStaticDataset(data.payload as StaticDataset);
        case AutomationEventTypes.LinkedDataset:
          return invalidateLinkedDataset(data.payload as LinkedDataset);
        case AutomationEventTypes.Run: {
          const run = data.payload as Run;
          return invalidateRun(run);
        }
        case AutomationEventTypes.Execution:
          return invalidateExecution(data.payload as Execution, data.id);
        case AutomationEventTypes.IntegrationActionSelectFieldOptions:
          return invalidateSelectFieldOptions(data.payload as SelectFieldOptionsPayload);
        default:
          console.warn('Unhandled event type:', type);
          return [];
      }
    },
    [
      invalidateAutomation,
      invalidateAutomationVersion,
      invalidateDataset,
      invalidateExecution,
      invalidateLinkedDataset,
      invalidateRun,
      invalidateSelectFieldOptions,
      invalidateStaticDataset,
    ],
  );

  const onEvent = useHandleEvent(getKeysToInvalidate);

  const onConnect = useCallback(async () => {
    void queryClient.invalidateQueries({ queryKey: STATIC_DATASET_USAGE_KEY(automationId) });
    void queryClient.invalidateQueries({ queryKey: STATIC_DATASETS_KEY(automationId) });
    void queryClient.invalidateQueries({ queryKey: ['static-revisions'] });
    void queryClient.invalidateQueries({ queryKey: ['static-version-resources'] });
    await queryClient.invalidateQueries({ queryKey: AUTOMATION_BASE_KEY(automationId) });
  }, [automationId, queryClient]);

  const url = `${import.meta.env.VITE_API_BASE_URL}/sse/events/automations/${automationId}`;

  useBaseSSE(eventTypes, onEvent, onConnect, url, enabled);
}
